import { OllieFloating } from '@/components/OllieFloating';
import { SpeechBubble } from '@/components/SpeechBubble';
import { UserDataProvider, useUserData } from '@/contexts/UserDataContext';
import { HoursADayStep } from '@/pages/Steps/HoursADayStep';
import { LostDaysPreviewStep } from '@/pages/Steps/LostDaysPreviewStep';
import { NumberOfDaysAnalogyStep } from '@/pages/Steps/NumberOfDaysAnalogyStep';
import { PhotoSelectionStep } from '@/pages/Steps/PhotoSelectionStep';
import { ProcessingStep } from '@/pages/Steps/ProcessingStep';
import { ShareStep } from '@/pages/Steps/ShareStep';
import { WelcomeStep } from '@/pages/Steps/WelcomeStep';
import { YearSelectionStep } from '@/pages/Steps/YearSelectionStep';
import type { ProcessedImage } from '@/utils/types';
import { useCallback, useMemo, useState } from 'react';
import { OcdFactStep } from './Steps/OcdFactStep';
import { YourOwnMessageStep } from './Steps/YourOwnMessageStep';

const MainContent = () => {
    const [currentState, setCurrentState] = useState(0);
    const [processedImages, setProcessedImages] = useState<ProcessedImage[]>([]);
    const { userData } = useUserData();

    const handleNext = useCallback(() => {
        setCurrentState(currentState + 1);
    }, [currentState]);

    const handleProcessingComplete = useCallback((images: ProcessedImage[]) => {
        setProcessedImages(images);
        setCurrentState(currentState + 1);
    }, [currentState]);

    const handleRetry = useCallback(() => {
        setProcessedImages([]);
        setCurrentState(8); // Go back to ProcessingStep
    }, []);

    const states = useMemo(() => [
        {
            text: "Hey, I'm Ollie, here to help you share your OCD story for OCD Awareness Month.",
            component: <WelcomeStep onNext={handleNext} />
        },
        {
            text: "Let's kick off by adding your own photo to show others it's your story.",
            component: <PhotoSelectionStep onNext={handleNext} />
        },
        {
            text: "If you had to pick, what year do you feel OCD first showed up for you?",
            component: <YearSelectionStep onNext={handleNext} />
        },
        {
            text: "In your toughest times, how many hours a day did OCD take with obsessing or rituals?",
            component: <HoursADayStep onNext={handleNext} />
        },
        {
            text: `A full year like that would be about ${userData.totalLostDays} days you might have used for other things.`,
            component: <LostDaysPreviewStep onNext={handleNext} />
        },
        {
            text: "That's like one of these analogies! Pick the one you want to add to your story.",
            component: <NumberOfDaysAnalogyStep onNext={handleNext} totalLostDays={userData.totalLostDays} />
        },
        {
            text: "Now pick the OCD fact that feels right to add to your story to bring awareness to OCD.",
            component: <OcdFactStep onNext={handleNext} />
        },
        {
            text: "Share a message for others struggling with OCD, your past self, or your followers and why it matters to you.",
            component: <YourOwnMessageStep onNext={handleNext} />
        },
        {
            text: "Creating the carousel now! While it loads, download our OCD app to help you in those tough moments!",
            component: <ProcessingStep onNext={handleProcessingComplete} />
        },
        {
            text: "Awesome! Your carousel is ready to share with the world! Don't forget to add #MyOCDStory to your post and share it with others ♥️️!",
            component: <ShareStep processedImages={processedImages} onRetry={handleRetry} />
        }
    ], [handleNext, handleProcessingComplete, handleRetry, processedImages, userData.totalLostDays]);

    return (
        <div className="w-full flex justify-center items-center">
            <div className="w-screen h-svh flex flex-col justify-between items-center gap-2 w-full max-w-[720px] pb-8 py-4 overflow-hidden overscroll-none">
                <div className="relative shrink-0 flex justify-start items-center w-full h-32 pr-4">
                    <OllieFloating />
                    <SpeechBubble text={states[currentState].text} animationDelay={0.3} />
                </div>
                {states[currentState]?.component}
            </div >
        </div>
    );
};

export const Main = () => {
    return (
        <UserDataProvider>
            <MainContent />
        </UserDataProvider>
    );
};